import { useEffect } from "react";
import { useUser } from "../../services/user-context";
import { useReports } from "../praxis-play-dashboard/context/reports-context";
import { PerformanceAnalytics } from "./components/PerformanceAnalytics";
import { SessionChart } from "./components/SessionChart";
import { SessionLogs } from "./components/SessionLogs";
import { NotificationButton } from "../../components/NotificationButton";
import { CallInsights } from "./components/CallInsights";
import { useTutorial } from "../../contexts/TutorialContext";
import { TutorialTooltip } from "../../components/TutorialTooltip";
import { ensurePraxisPlayNavVisible } from "../praxis-play-dashboard/utils";
import { CallStreak } from "../praxis-play-dashboard/components/CallStreak";
import { XPLimitBanner } from "./components/XPLimitBanner";
import { VersionReleaseCard } from "./components/VersionReleaseCard";
import { isBadgeCenterEnabled, isReleaseBannerEnabled } from "../../integrations/posthog/featureFlags";
import { usePostHogFeatureFlag } from "../../integrations/posthog/usePostHogFeatureFlag";

export function UserDashboard() {
  const { userReports, isLoadingReports } = useReports();
  const { userInfo } = useUser() as any;
  const sessions = Array.isArray(userReports) ? userReports : [];

  // Tutorial support - only for PraxisPlay
  const { nextStep, skip, getCurrentStepConfig, isVisible } = useTutorial();
  const currentStep = getCurrentStepConfig();
  const isBadgeCenterEnabledFlag = usePostHogFeatureFlag(isBadgeCenterEnabled);
  const isReleaseBannerEnabledFlag = usePostHogFeatureFlag(isReleaseBannerEnabled);
  
  // Expand sidebar when step 1 (modal) is shown so "Start Tour" doesn't cause layout reflow and Session Log blink
  useEffect(() => {
    if (!isVisible || currentStep?.id !== "step-1") return;
    ensurePraxisPlayNavVisible();
  }, [isVisible, currentStep?.id]);

  return (
    <div data-testid="user-dashboard" className="p-6 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-black text-[34px] font-semibold">Hi {userInfo?.firstName}</div>
          <div className="justify-start text-text-gray-500 text-[clamp(13px,1.5vw,16px)] font-normal">Here’s how your training is going</div>
        </div>
        <NotificationButton />
      </div>
      {isReleaseBannerEnabledFlag && <VersionReleaseCard />}
      {isBadgeCenterEnabledFlag && <XPLimitBanner />}
      <div className="flex gap-4">
        <div className="flex flex-col gap-4 flex-[3]">
          <div className="h-auto">
            <PerformanceAnalytics />
          </div>
          <div className="h-auto">
            <CallStreak />
          </div>
          <div className="items-stretch">
            <CallInsights />
          </div>
        </div>
        <div className="flex flex-col gap-4 flex-[4]">
          <div className="">
            <SessionChart />
          </div>
          <div className="items-stretch">
            <SessionLogs data={sessions?.slice(0, 6)} isLoadingReports={isLoadingReports} />
          </div>
        </div>
      </div>
      <TutorialTooltip
        step={currentStep}
        isVisible={isVisible}
        onAction={nextStep}
        onSkip={skip}
      />
    </div>
  );
}
